import { useCallback, useState } from "react";

import { BookingAnalyticsEvent } from "@/constants/analytics/Analytics";
import { BookingStepBaseTestIDMap } from "@/constants/Booking";
import useBookingAnalytics from "@/hooks/useBookingAnalytics";
import useBookingNavigation from "@/hooks/useBookingNavigation";
import { useAppSelector } from "@/store/hooks";
import {
  selectAppointmentTypeId,
  selectProviderType,
  useBookingDispatch,
} from "@/store/reducers/booking";
import BookingModalFooter from "@/ui/components/schedule/booking/BookingModalFooter";
import BookingOptionCard from "@/ui/components/schedule/booking/BookingOptionCard";
import type { BaseBookingStepProps } from "@/ui/components/schedule/booking/BookingStepMap";
import BookingStepTitle from "@/ui/components/schedule/booking/BookingStepTitle";
import { FlatList } from "@/ui/layout";
import { getUniqueAppointmentTypes } from "@/utils/booking.util";

const AppointmentSessionTypeStep = ({ step }: BaseBookingStepProps) => {
  const initialAppointmentTypeId = useAppSelector(selectAppointmentTypeId);
  const providerType = useAppSelector(selectProviderType);
  const { setAppointmentTypeId } = useBookingDispatch();
  const baseTestID = BookingStepBaseTestIDMap[step];
  const { trackEvent } = useBookingAnalytics();

  const [selectedSessionTypeId, setSelectedSessionTypeId] = useState<string>(
    initialAppointmentTypeId || "",
  );

  const { onBackwardNavigation, onForwardNavigation } = useBookingNavigation({
    currentStep: step,
  });

  // Get unique session types from the selected provider's appointment types
  const sessionTypeOptions = getUniqueAppointmentTypes(
    providerType?.appointmentTypes || [],
  );

  const handleForwardNavigation = () => {
    if (!selectedSessionTypeId) {
      return;
    }
    if (selectedSessionTypeId !== initialAppointmentTypeId) {
      setAppointmentTypeId(selectedSessionTypeId);
    }

    onForwardNavigation();
  };
  const ListHeaderComponent = useCallback(
    () => <BookingStepTitle step={step} />,
    [step],
  );

  return (
    <>
      <FlatList
        data={sessionTypeOptions}
        ListHeaderComponent={ListHeaderComponent}
        renderItem={({ item }) => {
          const isSelected =
            selectedSessionTypeId === item.appointmentTypeExtId;

          const handleSelectSessionType = () => {
            if (item.appointmentTypeExtId !== selectedSessionTypeId) {
              trackEvent({
                event: BookingAnalyticsEvent.SessionTypeSelection,
                source: step,
                value: item.appointmentType,
              });
            }
            setSelectedSessionTypeId(item.appointmentTypeExtId);
          };

          return (
            <BookingOptionCard
              id={item.appointmentTypeExtId}
              infoLabel={item.infoText}
              isSelected={isSelected}
              onSelect={handleSelectSessionType}
              title={item.appointmentType}
            />
          );
        }}
        testID={baseTestID}
      />
      <BookingModalFooter
        isForwardNavigationDisabled={!selectedSessionTypeId}
        onBackwardPress={onBackwardNavigation}
        onForwardPress={handleForwardNavigation}
        step={step}
      />
    </>
  );
};

export default AppointmentSessionTypeStep;
